'use client';

import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';

import { Button, Text } from '@chakra-ui/react';
import { Check, ExternalLink, Unlink, AlertCircle, FileText } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';

interface WorkspaceStatus {
  connected: boolean;
  connection: null | {
    id: string;
    googleEmail: string;
    status: string;
    scopes: string[];
    warnings: string[];
    connectedAt: string;
    lastUsedAt: string | null;
  };
}

export function GoogleWorkspaceIntegration() {
  const { user } = useAuth();
  const setup = useIntegrationSetupFlow(user?.uid);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState<WorkspaceStatus>({ connected: false, connection: null });

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/google-workspace/status', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load Workspace status');
      }
      const payload = (await response.json()) as WorkspaceStatus;
      setStatus(payload);
      return payload;
    } catch (error) {
      console.error('workspace status error', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('workspace_success');
    const warning = params.get('workspace_warning');
    const error = params.get('workspace_error');
    if (!success && !warning && !error) return;

    if (success === 'true') {
      toast.success('Google Workspace connected with drive.file scope.');
    }
    if (warning === 'unexpected_scope') {
      toast.warning('Workspace connected, but broader Drive scope was returned.');
    }
    if (error) {
      setup.reportError('Google Workspace connection failed. Please try again.');
    }

    params.delete('workspace_success');
    params.delete('workspace_warning');
    params.delete('workspace_error');
    const next = params.toString();
    const nextUrl = next ? `${window.location.pathname}?${next}` : window.location.pathname;
    window.history.replaceState({}, '', nextUrl);

    void fetchStatus().then(payload => {
      if (success === 'true' && !error && !warning && payload?.connected) setup.onConfigured();
    });
  }, [fetchStatus, setup.onConfigured]);

  const handleConnect = async () => {
    if (!user) return;
    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/google-workspace/start', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnTo: integrationSetupReturnPath('google_workspace', setup.returnTo, isAttuneDesktopShell()),
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.initiateUrl) {
        throw new Error(payload.error || 'Unable to start workspace OAuth');
      }
      window.location.href = payload.initiateUrl;
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Failed to start OAuth flow');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    setDisconnecting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/google-workspace/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to disconnect');
      }
      toast.success('Google Workspace disconnected');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Disconnect failed');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <IntegrationCard
        icon={<FileText size={18} />}
        title="Google Workspace"
        description="Export delivery to Docs and Sheets."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  const connection = status.connection;
  const hasWarning = connection?.status === 'warning' || (connection?.warnings?.length || 0) > 0;
  const connectedAt = connection?.connectedAt
    ? new Date(connection.connectedAt).toLocaleDateString()
    : null;

  return (
    <IntegrationCard
      icon={<FileText size={18} />}
      title="Google Workspace"
      description="Export delivery to Docs and Sheets."
      statusLabel={status.connected ? (hasWarning ? 'Warning' : 'Connected') : 'Not connected'}
      statusTone={status.connected ? (hasWarning ? 'warning' : 'connected') : 'idle'}
      action={
        status.connected ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            color="ui.text.tertiary"
            _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
          >
            {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
            <Text ml={1}>Disconnect</Text>
          </Button>
        ) : (
          <Button
            bg="accent.solid"
            color="accent.contrast"
            _hover={{ bg: 'accent.emphasized' }}
            borderRadius="glassButton"
            size="sm"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? <InlineSpinner size="xs" /> : <ExternalLink size={14} />}
            <Text ml={2}>{connecting ? 'Connecting...' : 'Connect'}</Text>
          </Button>
        )
      }
    >
      <IntegrationSetupError message={setup.error} />
      {status.connected && connection ? (
        <>
          <IntegrationStatusRow
            icon={hasWarning ? <AlertCircle size={16} /> : <Check size={16} />}
            title={connection.googleEmail || 'Google account'}
            description={connectedAt ? `Connected ${connectedAt}` : 'Connected'}
          />
          <IntegrationStatusRow
            icon={<FileText size={16} />}
            title="Drive scope"
            description={connection.scopes.join(', ') || 'unknown'}
          />
          {hasWarning && (
            <IntegrationNotice icon={<AlertCircle size={16} />} tone="warning">
              {connection.warnings?.join(' ') || 'Review this connection before exporting.'}
            </IntegrationNotice>
          )}
        </>
      ) : (
        <IntegrationStatusRow
          icon={<FileText size={16} />}
          title="Connect Workspace"
          description="Create Google Docs and Sheets from export jobs."
        />
      )}
    </IntegrationCard>
  );
}
